import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ConfirmDialog } from "./ConfirmDialog";

export const useMaster = (kind) => {
  const [list, setList] = useState([]);
  const load = useCallback(() => {
    api.get(`/masters/${kind}`).then((r) => setList(r.data)).catch(() => {});
  }, [kind]);
  useEffect(() => {
    load();
  }, [load]);
  return [list, load];
};

export const NameList = ({ id, list }) => (
  <datalist id={id}>{list.map((p) => <option key={p.id} value={p.name}>{p.phone}</option>)}</datalist>
);

const PartyDialog = ({ kind, label, party, onClose, onSaved }) => {
  const [f, setF] = useState({ name: party.name || "", phone: party.phone || "", gstin: party.gstin || "", address: party.address || "" });
  const [busy, setBusy] = useState(false);
  const t = `${kind}-dialog`;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (party.id) await api.put(`/masters/${kind}/${party.id}`, f);
      else await api.post(`/masters/${kind}`, f);
      toast.success(`${label} saved`);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const input = (k, l, extra = {}) => (
    <div><label className="label">{l}</label><input className="field" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${t}-${k}-input`} {...extra} /></div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-[95vw] overflow-y-auto sm:max-w-md" data-testid={t}>
        <DialogHeader><DialogTitle className="font-heading">{party.id ? `Edit ${label.toLowerCase()}` : `Add ${label.toLowerCase()}`}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {input("name", "Name *", { required: true })}
          <div className="grid grid-cols-2 gap-3">{input("phone", "Phone", { type: "tel" })}{input("gstin", "GSTIN", { maxLength: 15 })}</div>
          {input("address", "Address / city")}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="btn btn-outline" onClick={onClose} data-testid={`${t}-cancel-btn`}>Cancel</button>
            <button className="btn btn-primary" disabled={busy} data-testid={`${t}-save-btn`}>{busy && <Loader2 size={18} className="animate-spin" />} Save</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const PartiesTab = ({ kind, label, hint }) => {
  const { isAdmin } = useAuth();
  const [list, load] = useMaster(kind);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const remove = async () => {
    try {
      await api.delete(`/masters/${kind}/${deleting.id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDeleting(null);
  };
  return (
    <div className="space-y-3" data-testid={`${kind}-tab`}>
      <p className="text-xs text-muted-foreground">{hint}</p>
      <button className="btn btn-primary w-full" onClick={() => setEditing({})} data-testid={`add-${kind}-btn`}><Plus size={18} /> Add {label.toLowerCase()}</button>
      {!list.length && <p className="panel p-6 text-center text-muted-foreground" data-testid={`${kind}-empty`}>No {label.toLowerCase()}s yet.</p>}
      {list.map((p) => (
        <div key={p.id} className="panel flex items-start gap-3 p-4" data-testid={`${kind}-row-${p.name}`}>
          <div className="min-w-0 flex-1">
            <p className="font-semibold">{p.name}</p>
            <p className="break-words text-xs text-muted-foreground">{[p.phone, p.gstin && `GSTIN ${p.gstin}`, p.address].filter(Boolean).join(" · ")}</p>
          </div>
          <button className="icon-btn" onClick={() => setEditing(p)} aria-label="Edit" data-testid={`${kind}-edit-${p.name}`}><Pencil size={18} /></button>
          {isAdmin && <button className="icon-btn hover:!text-destructive" onClick={() => setDeleting(p)} aria-label="Delete" data-testid={`${kind}-delete-${p.name}`}><Trash2 size={18} /></button>}
        </div>
      ))}
      {editing && <PartyDialog kind={kind} label={label} party={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <ConfirmDialog open={!!deleting} onCancel={() => setDeleting(null)} onConfirm={remove} title={`Delete ${deleting?.name}?`} body="Past entries keep the name as it was." />
    </div>
  );
};

export const CustomersTab = () => <PartiesTab kind="customers" label="Customer" hint="Customers are picked in Record sale. New names typed in a sale are added here automatically." />;
export const TransportersTab = () => <PartiesTab kind="transporters" label="Transporter" hint="Transporters are picked in GRN and delivery challans." />;
